/**
 * Content Studio admin queries — Sunday Engine v2 Studio MVP.
 *
 * Reads + mutations for the workflow at /admin/studio:
 *   - content_ideas (My Ideas column on the Ideas tab + Plan tab detail)
 *   - weekly_brief (External Signal column — read-only subset)
 *   - content_drafts (current draft for an idea, latest version)
 *
 * Edge-function invocations (content-generate-outline et al.) wrap
 * invokeEdgeJson with shape-aware error handling — admin gets a useful
 * banner instead of a generic "something went wrong".
 *
 * Spec: specs/sunday-engine-v2.md "Content Studio MVP" Step 6.
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeJson } from '../_edge'
import {
  CONTENT_GENERATE_OUTLINE,
  CONTENT_GENERATE_SCRIPT,
  CONTENT_REWRITE_SECTION,
  CONTENT_REWRITE_FULL,
  CONTENT_INTERVIEW_PROMPT,
  CONTENT_EXTRACT_PATTERN,
  CONTENT_ATOMIZE_LONGFORM,
} from '../_endpoints'

const IDEAS_PAGE_SIZE = 100
const EXTERNAL_SIGNAL_PAGE_SIZE = 60

const IDEA_COLS = `id, title, source, format_hint, theme, status,
  voice_profile_pref, resonance_score, source_url, source_creator,
  source_platform, mason_note, cold_stored_at, killed_at,
  created_at, updated_at`

const DRAFT_COLS = `id, idea_id, format, outline, script_text,
  version_number, voice_profile, register, applied_template_id,
  matrix_answers, matrix_passed, partial_answers,
  generation_cost_usd, approved_at,
  scheduled_at, scheduled_platform,
  posted_at, posted_url, posted_external_id,
  created_at, updated_at`

/**
 * Fetch My Ideas — open + planned + scripted ideas, excluding cold-stored
 * and killed. Sorted newest first. The Studio Ideas tab consumes this.
 *
 * Pass an explicit `statuses` array to override the default working set
 * (e.g. to surface the Graveyard view in Step 12).
 */
export async function fetchMyIdeas({
  statuses = ['open', 'planned', 'scripted', 'queued', 'scheduled', 'posted'],
  limit = IDEAS_PAGE_SIZE,
} = {}) {
  const { data, error } = await supabase
    .from('content_ideas')
    .select(IDEA_COLS)
    .in('status', statuses)
    .is('cold_stored_at', null)
    .is('killed_at', null)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Fetch one idea by id. Returns null on 404 (PostgREST PGRST116) so the
 * Plan tab can render a "pick an idea" state instead of throwing.
 */
export async function fetchIdeaById(id) {
  if (!id) return null
  const { data, error } = await supabase
    .from('content_ideas')
    .select(IDEA_COLS)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data || null
}

/**
 * Update an existing idea — title, mason_note, format_hint, theme,
 * voice_profile_pref. Mason ask 2026-05-12: "let me edit my ideas after
 * I capture them." Patches only the fields you pass; null values are
 * intentional (lets Mason clear a note).
 */
export async function updateIdea(id, patch = {}) {
  if (!id) throw new Error('idea id is required')
  const allowed = ['title', 'mason_note', 'format_hint', 'theme', 'voice_profile_pref']
  const clean = {}
  for (const k of allowed) {
    if (k in patch) {
      const v = patch[k]
      clean[k] = typeof v === 'string' ? v.trim() || null : v
    }
  }
  if (clean.title === null || clean.title === '') {
    throw new Error('Title cannot be empty.')
  }
  if (Object.keys(clean).length === 0) return null
  const { data, error } = await supabase
    .from('content_ideas')
    .update(clean)
    .eq('id', id)
    .select(IDEA_COLS)
    .single()
  if (error) throw error
  return data
}

/**
 * Capture a new idea from the My Ideas input. `source` defaults to 'manual'
 * (typed in the Studio); other sources come from mobile capture, external
 * promotion, or the 1-to-N atomizer.
 */
export async function createIdea({
  title,
  source = 'manual',
  formatHint = null,
  theme = null,
  voiceProfilePref = null,
  masonNote = null,
  sourceUrl = null,
  sourceCreator = null,
  sourcePlatform = null,
} = {}) {
  const trimmed = (title || '').trim()
  if (!trimmed) throw new Error('Idea title is required.')
  const { data, error } = await supabase
    .from('content_ideas')
    .insert({
      title: trimmed,
      source,
      format_hint: formatHint,
      theme,
      voice_profile_pref: voiceProfilePref,
      mason_note: masonNote,
      source_url: sourceUrl,
      source_creator: sourceCreator,
      source_platform: sourcePlatform,
    })
    .select(IDEA_COLS)
    .single()
  if (error) throw error
  return data
}

/**
 * External Signal — last 14 days of weekly_brief rows that haven't been
 * skipped. Display order on the card is What (hook) · Who (creator) ·
 * Where (platform) per locked rule 3. Theology filter ran at ingest time
 * (layer-1-discovery.yml), so anything here is already safe to surface.
 */
export async function fetchExternalSignal({ limit = EXTERNAL_SIGNAL_PAGE_SIZE } = {}) {
  const cutoff = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const { data, error } = await supabase
    .from('weekly_brief')
    .select(
      `id, source, platform, source_url, hook_text, creator_handle,
      creator_followers, views, view_ratio, outlier_tier, post_age_hours,
      topic_lifecycle, why_outlier, can_we_do_this, suggested_angle,
      format_type, length_seconds,
      run_date, status, created_at`
    )
    .gte('run_date', cutoff)
    .neq('status', 'skipped')
    .order('view_ratio', { ascending: false, nullsFirst: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Promote a weekly_brief row to a new content_ideas row. The brief row's
 * status flips to 'picked' so the External Signal column can dim it. The
 * promotion carries the brief's hook text + creator/platform metadata.
 */
export async function promoteExternalSignal(briefRow) {
  const idea = await createIdea({
    title: briefRow.hook_text || briefRow.suggested_angle || '(untitled)',
    source: 'external',
    sourceUrl: briefRow.source_url || null,
    sourceCreator: briefRow.creator_handle || null,
    sourcePlatform: briefRow.platform || null,
    masonNote: briefRow.suggested_angle || null,
  })
  const { error } = await supabase
    .from('weekly_brief')
    .update({ status: 'picked' })
    .eq('id', briefRow.id)
  if (error) {
    // Idea was created but brief status didn't flip. Non-fatal — user
    // sees the new idea; the brief row stays 'new'. Log so it's greppable.
    console.warn('promoteExternalSignal: idea created but brief status update failed:', error)
  }
  return idea
}

/**
 * Fetch one draft by id. Used by the Script tab to re-hydrate the outline
 * preview after a reload — the in-memory `latestOutline` in StudioAdmin
 * dies with the page session, but `?draftId=` survives in the URL.
 */
export async function fetchDraftById(id) {
  if (!id) return null
  const { data, error } = await supabase
    .from('content_drafts')
    .select(DRAFT_COLS)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data || null
}

/**
 * Fetch the latest draft for an idea (highest version_number). Returns
 * null when no draft exists yet — Plan tab uses that to show the "no
 * draft yet, generate the outline" affordance.
 */
export async function fetchLatestDraftForIdea(ideaId) {
  if (!ideaId) return null
  const { data, error } = await supabase
    .from('content_drafts')
    .select(DRAFT_COLS)
    .eq('idea_id', ideaId)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return data || null
}

/**
 * Fetch every version row for an idea, newest first. Used by the Script
 * tab's version history strip — clicking an older version chip loads it
 * into the editor (saving from there creates a new version on top).
 *
 * Returns a slim shape (no full outline/script_text) so the chip strip
 * stays cheap to render. Editor calls fetchDraftById to hydrate a chosen
 * version when the user clicks one.
 */
export async function fetchAllVersionsForIdea(ideaId) {
  if (!ideaId) return []
  const { data, error } = await supabase
    .from('content_drafts')
    .select('id, version_number, generation_cost_usd, approved_at, created_at')
    .eq('idea_id', ideaId)
    .order('version_number', { ascending: false })
  if (error) throw error
  return data || []
}

/**
 * Save text-only edits to an existing draft — script body, outline tweak,
 * hook adjustment. Does NOT create a new version (the spec's "every regen
 * creates a version" rule applies to AI generations, not manual edits).
 *
 * Pass `outline` to update the JSONB outline column; pass `scriptText`
 * to update the script body. Either or both. Empty patches are a no-op.
 */
export async function updateDraftScript({ draftId, scriptText, outline } = {}) {
  if (!draftId) throw new Error('draftId is required')
  const patch = {}
  if (scriptText !== undefined) patch.script_text = scriptText
  if (outline !== undefined) patch.outline = outline
  if (Object.keys(patch).length === 0) return null
  const { data, error } = await supabase
    .from('content_drafts')
    .update(patch)
    .eq('id', draftId)
    .select('id, script_text, outline, updated_at')
    .single()
  if (error) throw error
  return data
}

/**
 * Templates — saved-as-template structures Mason can apply to future
 * drafts. Listed newest-first with applied_count for prioritization.
 */
export async function fetchTemplates({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('content_templates')
    .select('id, name, source_draft_id, structure, applied_count, created_at')
    .order('applied_count', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Promote a draft to a reusable template. Captures outline + voice +
 * register + format as the template's `structure` JSONB. The source
 * draft's id is stored as a plain uuid (no FK — see migration 20260511150000
 * comment on content_templates.source_draft_id).
 */
export async function createTemplate({ name, sourceDraftId, structure } = {}) {
  const trimmed = (name || '').trim()
  if (!trimmed) throw new Error('Template name is required.')
  if (!sourceDraftId) throw new Error('sourceDraftId is required.')
  const { data, error } = await supabase
    .from('content_templates')
    .insert({
      name: trimmed,
      source_draft_id: sourceDraftId,
      structure: structure ?? {},
    })
    .select('id, name, source_draft_id, structure, applied_count, created_at')
    .single()
  if (error) throw error
  return data
}

/**
 * Apply a template to an existing draft — copies the template's structure
 * (outline + voice_profile + register + format if present) onto the draft
 * and increments the template's applied_count for the lifecycle sort.
 *
 * Returns the updated draft row. Caller refreshes the editor state.
 */
export async function applyTemplateToDraft({ templateId, draftId } = {}) {
  if (!templateId) throw new Error('templateId is required.')
  if (!draftId) throw new Error('draftId is required.')

  // 1. Read the template's structure
  const { data: tpl, error: tplErr } = await supabase
    .from('content_templates')
    .select('id, structure, applied_count')
    .eq('id', templateId)
    .single()
  if (tplErr) throw tplErr
  if (!tpl) throw new Error('Template not found.')

  const structure = tpl.structure || {}
  const patch = {}
  if (structure.outline !== undefined) patch.outline = structure.outline
  if (structure.format !== undefined) patch.format = structure.format
  if (structure.voice_profile !== undefined) patch.voice_profile = structure.voice_profile
  if (structure.register !== undefined) patch.register = structure.register
  patch.applied_template_id = templateId

  // 2. Patch the draft
  const { data: updated, error: updateErr } = await supabase
    .from('content_drafts')
    .update(patch)
    .eq('id', draftId)
    .select(DRAFT_COLS)
    .single()
  if (updateErr) throw updateErr

  // 3. Bump applied_count (best-effort — non-fatal if it fails)
  const { error: bumpErr } = await supabase
    .from('content_templates')
    .update({ applied_count: (tpl.applied_count ?? 0) + 1 })
    .eq('id', templateId)
  if (bumpErr) console.warn('applyTemplateToDraft: applied_count bump failed:', bumpErr)

  return updated
}

/**
 * Generate the outline for an idea via the deployed Haiku edge function.
 * On success the backend inserts a new content_drafts row (version 1)
 * and returns the draft id + outline JSON.
 *
 * Pass `signal` to abort on tab/idea change — prevents the stale-write
 * bug where an outline lands on a no-longer-active idea's draft.
 */
export async function generateOutline({ ideaId, format, voiceProfile, register = null, signal }) {
  return invokeEdgeJson(
    CONTENT_GENERATE_OUTLINE,
    {
      idea_id: ideaId,
      format,
      voice_profile: voiceProfile,
      register,
    },
    { errorMessage: 'Outline generation failed.', signal }
  )
}

/**
 * Generate the script from an existing draft's outline. Updates the same
 * row if `script_text` was NULL (first fill-in); otherwise creates a new
 * version via the atomic RPC.
 *
 * Wired but not invoked from Step 6 — Plan tab only fires generate-outline.
 * Script tab (Step 7) calls this one.
 */
/**
 * Generate the script body. Optional knobs let the user steer the model
 * BEFORE generation:
 *   - customInstructions: free-form direction ("write it like a sermon
 *     but for X audience")
 *   - targetLength: free-form duration/length hint ("30 seconds",
 *     "800 words", "5 posts")
 *   - includeElements: array of preset element ids to weave in
 *     ('scripture' | 'personal_story' | 'statistic' | 'cta' |
 *      'question_opener' | 'metaphor')
 *
 * All optional. Edge fn injects whichever are present into the user
 * message; voice + banned + patterns stay in the cached system block.
 */
export async function generateScript({
  draftId,
  customInstructions = null,
  targetLength = null,
  includeElements = null,
  signal,
} = {}) {
  return invokeEdgeJson(
    CONTENT_GENERATE_SCRIPT,
    {
      draft_id: draftId,
      custom_instructions: customInstructions,
      target_length: targetLength,
      include_elements: includeElements,
    },
    { errorMessage: 'Script generation failed.', signal }
  )
}

export async function rewriteSection({ draftId, selectedText, instruction, signal } = {}) {
  return invokeEdgeJson(
    CONTENT_REWRITE_SECTION,
    {
      draft_id: draftId,
      selected_text: selectedText,
      instruction,
    },
    { errorMessage: 'Section rewrite failed.', signal }
  )
}

export async function rewriteFull({ draftId, sidebarNotes, signal } = {}) {
  return invokeEdgeJson(
    CONTENT_REWRITE_FULL,
    {
      draft_id: draftId,
      sidebar_notes: sidebarNotes,
    },
    { errorMessage: 'Full rewrite failed.', signal }
  )
}

/**
 * Generate 3 interview questions for an idea (Haiku). Powers the "Interview
 * Me First" toggle in the Plan tab — Mason answers before generating the
 * script so the model has tighter direction.
 *
 * Returns { questions: string[3] }.
 */
export async function generateInterviewQuestions({ ideaId, signal } = {}) {
  return invokeEdgeJson(
    CONTENT_INTERVIEW_PROMPT,
    { idea_id: ideaId },
    { errorMessage: 'Interview prompt generation failed.', signal }
  )
}

/**
 * Extract a structural pattern from a transcript (Sonnet). Inserts a row
 * into `learned_patterns` with hook_archetype + beat_structure + pacing +
 * cta_style. Used by:
 *   - "Learn from this" on External Signal cards (source_type='external_creator')
 *   - Step 11 auto-promotion of own posted-content outliers (source_type='own_post')
 */
export async function extractPattern({
  sourceType,
  sourceUrl = null,
  sourceCreator = null,
  sourcePlatform = null,
  transcript,
  masonNote = null,
  signal,
} = {}) {
  return invokeEdgeJson(
    CONTENT_EXTRACT_PATTERN,
    {
      source_type: sourceType,
      source_url: sourceUrl,
      source_creator: sourceCreator,
      source_platform: sourcePlatform,
      transcript,
      mason_note: masonNote,
    },
    { errorMessage: 'Pattern extraction failed.', signal }
  )
}

/**
 * Atomize a posted long-form into 8-12 short-form derivative idea seeds
 * (Sonnet). Inserts that many `content_ideas` rows with source='atomized'
 * and (when known) source_url/source_creator pointing back at the parent.
 *
 * Used by the Atomizer button on the Library tab (Step 12).
 */
export async function atomizeLongform({
  sourceIdeaId = null,
  sourceUrl = null,
  sourceCreator = null,
  sourcePlatform = null,
  transcript,
  signal,
} = {}) {
  return invokeEdgeJson(
    CONTENT_ATOMIZE_LONGFORM,
    {
      source_idea_id: sourceIdeaId,
      source_url: sourceUrl,
      source_creator: sourceCreator,
      source_platform: sourcePlatform,
      transcript,
    },
    { errorMessage: 'Atomization failed.', signal }
  )
}

/**
 * Kill an idea (soft-style for the Graveyard view per spec rule 8 — killed
 * ideas live forever, never hard-deleted). Sets `killed_at` and `status`.
 *
 * Wired from the IdeaCard kebab menu (Step 6 follow-up) + the Matrix-Gate
 * Kill button (Step 9). Graveyard view in Step 12 reads `status='killed'`.
 */
export async function killIdea(id, reason = null) {
  // Only overwrite mason_note when caller passes a non-empty kill reason.
  // Without this guard, a `reason: null` caller would wipe a meaningful
  // note that was captured during plan/script (Step 9 audit catch).
  const patch = {
    status: 'killed',
    killed_at: new Date().toISOString(),
  }
  if (typeof reason === 'string' && reason.trim().length > 0) {
    patch.mason_note = reason.trim()
  }
  const { error } = await supabase.from('content_ideas').update(patch).eq('id', id)
  if (error) throw error
}

/**
 * Audience-Problem Matrix submit (Step 9). Mason just answered all 3
 * required fields and clicked "Send to Queue":
 *   - content_drafts.matrix_answers = the 3 fields (and any optional 4th)
 *   - content_drafts.matrix_passed  = true
 *   - content_drafts.approved_at    = now
 *   - content_ideas.status          = 'queued' (the idea moves stage)
 *
 * The two updates aren't transactional — if the draft update lands but
 * the idea status flip fails, the draft is "approved" while the idea is
 * still 'scripted'. The Queue view in Step 13 reads from approved_at on
 * the draft, not idea.status, so an approved-but-unflipped pair is still
 * correctly visible in the queue. The status flip is best-effort
 * cosmetic for the Ideas tab tile.
 *
 * Guards (Step 9 audit):
 * - Stale-version: if `expectedVersionNumber` is provided, throw
 *   `stale_version` when the latest draft for this idea has a higher
 *   version_number — Mason must reload to approve the newest draft.
 * - Idempotent: `.is('approved_at', null)` filter so a network-blip
 *   retry can't overwrite a real approved_at timestamp.
 * - Partial nulled LAST: only after the idea status flip succeeds, so
 *   a mid-flow failure leaves the parked answers recoverable.
 */
export async function submitMatrixAnswers({
  draftId,
  ideaId,
  answers,
  expectedVersionNumber,
} = {}) {
  if (!draftId) throw new Error('draftId is required')
  if (!ideaId) throw new Error('ideaId is required')
  if (!answers || typeof answers !== 'object') throw new Error('answers is required')

  // Stale-version guard. Cheap single query; runs only when the caller
  // opted in by passing expectedVersionNumber.
  if (typeof expectedVersionNumber === 'number') {
    const { data: latest, error: latestErr } = await supabase
      .from('content_drafts')
      .select('id, version_number')
      .eq('idea_id', ideaId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (latestErr) throw latestErr
    if (latest && latest.version_number > expectedVersionNumber) {
      throw new Error('stale_version')
    }
  }

  // Idempotent approve: filter on approved_at IS NULL. A retried submit
  // returns null `data` (no row matched), which we then re-read as the
  // already-approved row so the caller still gets a usable draft back.
  const { data: updated, error: draftErr } = await supabase
    .from('content_drafts')
    .update({
      matrix_answers: answers,
      matrix_passed: true,
      approved_at: new Date().toISOString(),
    })
    .eq('id', draftId)
    .is('approved_at', null)
    .select(DRAFT_COLS)
    .maybeSingle()
  if (draftErr) throw draftErr

  let approvedDraft = updated
  if (!approvedDraft) {
    // Retry-after-success path. Re-fetch and treat as a no-op success.
    approvedDraft = await fetchDraftById(draftId)
    if (!approvedDraft || !approvedDraft.approved_at) {
      throw new Error('Could not approve the draft.')
    }
  }

  const { error: ideaErr } = await supabase
    .from('content_ideas')
    .update({ status: 'queued' })
    .eq('id', ideaId)
  if (ideaErr) {
    console.warn(
      'submitMatrixAnswers: draft approved but idea status flip failed:',
      ideaErrSafe(ideaErr)
    )
  }

  // Clear partials only after both writes settle. A separate update so
  // the row we return reflects the final state.
  if (approvedDraft.partial_answers) {
    const { data: cleared, error: clearErr } = await supabase
      .from('content_drafts')
      .update({ partial_answers: null })
      .eq('id', draftId)
      .select(DRAFT_COLS)
      .single()
    if (clearErr) {
      console.warn('submitMatrixAnswers: partial_answers clear failed (non-fatal):', clearErr)
    } else if (cleared) {
      approvedDraft = cleared
    }
  }

  return approvedDraft
}

function ideaErrSafe(err) {
  // Defensive log helper — never throw in the warn path.
  try {
    return err?.message || String(err)
  } catch {
    return 'unknown'
  }
}

/**
 * Audience-Problem Matrix park (Step 9). Mason typed something but picked
 * "Send Back to Script" instead of submitting. Persist the partial JSONB
 * so the modal can re-prefill on next open without losing his work.
 */
export async function parkMatrixPartial({ draftId, partial } = {}) {
  if (!draftId) throw new Error('draftId is required')
  const { error } = await supabase
    .from('content_drafts')
    .update({ partial_answers: partial ?? null })
    .eq('id', draftId)
  if (error) throw error
}

// ── Step 10: Pattern Library ──────────────────────────────────────────

const PATTERN_COLS = `id, source_type, source_url, source_creator,
  source_platform, hook_archetype, beat_structure, pacing_notes, cta_style,
  mason_note, applied_count, pinned, stale_at, created_at`

/**
 * Fetch learned_patterns for the Library tab. `filter` selects which
 * subset of the lifecycle to show:
 *   - 'active'  (default) — pinned first, then non-stale within 180d
 *   - 'pinned'            — pinned only (forever)
 *   - 'stale'             — has stale_at flag (90d untouched, 180d total)
 *   - 'all'               — every row, including aged-out non-pinned
 *
 * Sort matches the script generator's blender: pinned DESC,
 * applied_count DESC, created_at DESC.
 */
export async function fetchLearnedPatterns({ filter = 'active', limit = 100 } = {}) {
  let q = supabase.from('learned_patterns').select(PATTERN_COLS)
  if (filter === 'pinned') {
    q = q.eq('pinned', true)
  } else if (filter === 'stale') {
    q = q.not('stale_at', 'is', null)
  } else if (filter === 'active') {
    // Mirrors content-generate-script blender: pinned OR (not stale AND under 180d).
    const cutoff = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString()
    q = q.or(`pinned.eq.true,and(stale_at.is.null,created_at.gt.${cutoff})`)
  }
  // 'all' falls through with no extra filter.
  const { data, error } = await q
    .order('pinned', { ascending: false })
    .order('applied_count', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Toggle a pattern's pinned state. Pinning also clears stale_at — a
 * pattern Mason explicitly endorses shouldn't carry the "untouched"
 * flag from the cron's last sweep.
 */
export async function togglePatternPinned(id, nextPinned) {
  if (!id) throw new Error('pattern id is required')
  const patch = { pinned: !!nextPinned }
  if (nextPinned) patch.stale_at = null
  const { data, error } = await supabase
    .from('learned_patterns')
    .update(patch)
    .eq('id', id)
    .select(PATTERN_COLS)
    .single()
  if (error) throw error
  return data
}

/**
 * Hard-delete a pattern. Used by the Library "trash" affordance for
 * mistakes (wrong creator extracted, garbled JSON). Patterns are
 * narrow blender inputs — there's no soft-delete history value here
 * the way there is for content_ideas + the Graveyard view.
 */
export async function deletePattern(id) {
  if (!id) throw new Error('pattern id is required')
  const { error } = await supabase.from('learned_patterns').delete().eq('id', id)
  if (error) throw error
}

// ── Step 11: Posted → outlier auto-promotion prompts ──────────────────

/**
 * Fetch content_posts rows that the daily-analytics-pull cron flagged
 * as outliers worth learning a pattern from. Returns newest-flagged
 * first so the most recent overperformer surfaces at the top of the
 * Patterns sub-tab banner.
 *
 * `transcript` is included so the Learn-from-this modal can pre-fill
 * without a second roundtrip; `title` + `external_id` + signals power
 * the prompt label.
 */
export async function fetchPendingOutlierPromotions({ limit = 10 } = {}) {
  const { data, error } = await supabase
    .from('content_posts')
    .select(
      `id, external_id, platform, title, posted_at, views,
       outlier_promote_signals, outlier_promote_flagged_at, transcript`
    )
    .eq('outlier_promote_status', 'pending')
    .order('outlier_promote_flagged_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Mason said "no" — flip the prompt to dismissed so the cron stops
 * resurfacing this video. (Cron only flags rows where status IS NULL,
 * so 'dismissed' sticks across runs.)
 */
export async function dismissOutlierPromotion(postId) {
  if (!postId) throw new Error('post id is required')
  const { error } = await supabase
    .from('content_posts')
    .update({ outlier_promote_status: 'dismissed' })
    .eq('id', postId)
  if (error) throw error
}

/**
 * Mason ran extract-pattern via the Learn-from-this modal — flip the
 * prompt to 'learned' so it disappears from the banner. The actual
 * pattern row was created by extractPattern; this just records that
 * THIS post was the source.
 */
export async function markOutlierPromotionLearned(postId) {
  if (!postId) throw new Error('post id is required')
  const { error } = await supabase
    .from('content_posts')
    .update({ outlier_promote_status: 'learned' })
    .eq('id', postId)
  if (error) throw error
}

// ── Step 12: Cold Storage / Graveyard / Atomizer ──────────────────────

/**
 * Cold storage — ideas the daily cron flagged as untouched 60+ days.
 * Searchable but hidden from the working My Ideas list. Newest-cold
 * first so what just fell off the active list surfaces at top.
 */
export async function fetchColdStorageIdeas({ limit = IDEAS_PAGE_SIZE } = {}) {
  const { data, error } = await supabase
    .from('content_ideas')
    .select(IDEA_COLS)
    .not('cold_stored_at', 'is', null)
    .is('killed_at', null)
    .order('cold_stored_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Graveyard — soft-killed ideas (status='killed', killed_at set). Never
 * hard-deleted per locked rule 8. Newest-killed first.
 */
export async function fetchGraveyardIdeas({ limit = IDEAS_PAGE_SIZE } = {}) {
  const { data, error } = await supabase
    .from('content_ideas')
    .select(IDEA_COLS)
    .eq('status', 'killed')
    .not('killed_at', 'is', null)
    .order('killed_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Restore a cold-stored idea back to the active working set. Clears
 * cold_stored_at + bumps updated_at (so the next cron sweep doesn't
 * re-flag it the same day).
 */
export async function restoreFromColdStorage(id) {
  if (!id) throw new Error('idea id is required')
  const { error } = await supabase
    .from('content_ideas')
    .update({ cold_stored_at: null, updated_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

/**
 * Restore a killed idea from the Graveyard. Clears killed_at, sets
 * status back to 'open'. Mason rarely does this — kills are usually
 * intentional — but the affordance keeps the soft-delete promise of
 * locked rule 8 ("never hard-deleted") meaningful.
 */
export async function restoreFromGraveyard(id) {
  if (!id) throw new Error('idea id is required')
  const { error } = await supabase
    .from('content_ideas')
    .update({
      killed_at: null,
      status: 'open',
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
  if (error) throw error
}

// ── Step 13: Queue + Calendar + Mark Posted ───────────────────────────

const QUEUE_COLS = `id, idea_id, format, voice_profile, register,
  version_number, approved_at, scheduled_at, scheduled_platform,
  posted_at, posted_url, posted_external_id`

/**
 * Fetch drafts in the queue: matrix-passed, not yet scheduled, not yet
 * posted. Joined to content_ideas for the title + theme. Newest-approved
 * first (most-recent decisions surface first).
 */
export async function fetchQueueDrafts({ limit = 100 } = {}) {
  const { data, error } = await supabase
    .from('content_drafts')
    .select(`${QUEUE_COLS}, idea:content_ideas (id, title, theme, source)`)
    .eq('matrix_passed', true)
    .is('scheduled_at', null)
    .is('posted_at', null)
    .order('approved_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Fetch drafts already scheduled in a date range — powers the Calendar
 * grid. Date strings in ISO format (YYYY-MM-DD); inclusive on both ends.
 */
export async function fetchScheduledDrafts({ from, to } = {}) {
  if (!from || !to) throw new Error('from + to date strings required')
  const { data, error } = await supabase
    .from('content_drafts')
    .select(`${QUEUE_COLS}, idea:content_ideas (id, title, theme, source)`)
    .gte('scheduled_at', from)
    .lt('scheduled_at', to)
    .is('posted_at', null)
    .order('scheduled_at', { ascending: true })
  if (error) throw error
  return data || []
}

/**
 * Fetch drafts already posted in a date range — Calendar shows these
 * as completed slots so Mason can see his actual posting cadence.
 */
export async function fetchPostedDrafts({ from, to } = {}) {
  if (!from || !to) throw new Error('from + to date strings required')
  const { data, error } = await supabase
    .from('content_drafts')
    .select(`${QUEUE_COLS}, idea:content_ideas (id, title, theme, source)`)
    .gte('posted_at', from)
    .lt('posted_at', to)
    .order('posted_at', { ascending: true })
  if (error) throw error
  return data || []
}

/**
 * Fetch content_posts in a date range — independent of any draft tie.
 * Powers the Calendar's "everything I've posted" view: surfaces YT/IG/TT/X
 * posts that pre-date Studio (or where Mason posted without first writing
 * a draft). De-duped against draft-tied posts via posted_external_id so
 * a single video doesn't render twice.
 *
 * Mason ask 2026-05-12: "backfill my channels and put them on the calendar
 * so I can see what I've posted already."
 */
export async function fetchPostedContent({ from, to } = {}) {
  if (!from || !to) throw new Error('from + to date strings required')
  // posted_at is `date` on content_posts (not timestamptz). The from/to
  // strings come in as YYYY-MM-DD already so direct compare works.
  const { data, error } = await supabase
    .from('content_posts')
    .select('id, platform, external_id, title, posted_at, link, views, likes, comments, format')
    .gte('posted_at', from)
    .lt('posted_at', to)
    .order('posted_at', { ascending: true })
  if (error) throw error
  return data || []
}

/**
 * Weekly metrics roll-up — sum + best for content_posts in a date range.
 * Powers the Calendar's at-a-glance "this week" bar (replaces the old
 * ContentTrackerAdmin KPI cards). Pure client-side aggregate so the
 * Calendar can derive metrics from already-fetched fetchPostedContent
 * rows without a second roundtrip.
 */
export function aggregateWeeklyMetrics(posts) {
  if (!Array.isArray(posts) || posts.length === 0) {
    return { total: 0, views: 0, likes: 0, comments: 0, best: null, byPlatform: {} }
  }
  let views = 0
  let likes = 0
  let comments = 0
  let best = null
  const byPlatform = {}
  for (const p of posts) {
    const v = Number(p.views) || 0
    const l = Number(p.likes) || 0
    const c = Number(p.comments) || 0
    views += v
    likes += l
    comments += c
    if (!best || v > (Number(best.views) || 0)) best = p
    const plat = p.platform || 'other'
    if (!byPlatform[plat]) byPlatform[plat] = { count: 0, views: 0 }
    byPlatform[plat].count += 1
    byPlatform[plat].views += v
  }
  return { total: posts.length, views, likes, comments, best, byPlatform }
}

/**
 * Schedule a draft to a specific date+time on a platform. Idempotent —
 * re-scheduling overwrites scheduled_at + scheduled_platform; the draft
 * stays approved and unposted regardless.
 */
export async function scheduleDraft({ draftId, scheduledAt, platform } = {}) {
  if (!draftId) throw new Error('draftId is required')
  if (!scheduledAt) throw new Error('scheduledAt is required')
  if (!platform) throw new Error('platform is required')
  const { data, error } = await supabase
    .from('content_drafts')
    .update({ scheduled_at: scheduledAt, scheduled_platform: platform })
    .eq('id', draftId)
    .eq('matrix_passed', true) // can only schedule approved drafts
    .is('posted_at', null)
    .select(QUEUE_COLS)
    .single()
  if (error) throw error
  return data
}

/**
 * Pull a draft back to the queue — clears scheduled_at + platform.
 */
export async function unscheduleDraft(draftId) {
  if (!draftId) throw new Error('draftId is required')
  const { error } = await supabase
    .from('content_drafts')
    .update({ scheduled_at: null, scheduled_platform: null })
    .eq('id', draftId)
    .is('posted_at', null)
  if (error) throw error
}

/**
 * Extract the YouTube video ID from a posted URL (or null if not YouTube).
 * Handles both watch?v= and youtu.be/ formats.
 */
function extractYoutubeId(url) {
  if (!url) return null
  const patterns = [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pat of patterns) {
    const m = url.match(pat)
    if (m) return m[1]
  }
  return null
}

/**
 * Mark a draft posted. Sets posted_at + posted_url + derived
 * posted_external_id; flips parent idea's status to 'posted'; upserts
 * a row into content_posts so the daily-analytics-pull cron picks it up
 * for metrics refresh on the next run.
 *
 * NOT transactional — three independent writes, any of which can fail
 * independently. Per Step 13 audit, partial failures now surface in the
 * return shape as `warnings: string[]` so the modal can show them
 * instead of silently swallowing into console. The draft flip is the
 * load-bearing one; if it fails the function throws. Idea-status and
 * content_posts upsert failures are non-fatal but visible.
 *
 * `posted_at` write to content_posts coerces to YYYY-MM-DD because that
 * column is `date` (not timestamptz) per the original content_posts
 * schema; passing a full ISO timestamp lets PG implicit-cast but is
 * fragile. Step 13 audit catch.
 */
export async function markDraftPosted({ draftId, ideaId, postedUrl, platform } = {}) {
  if (!draftId) throw new Error('draftId is required')
  if (!ideaId) throw new Error('ideaId is required')
  if (!postedUrl) throw new Error('postedUrl is required')
  if (!platform) throw new Error('platform is required')

  const externalId = platform === 'youtube' ? extractYoutubeId(postedUrl) : null
  const nowIso = new Date().toISOString()
  const nowDate = nowIso.slice(0, 10)
  const warnings = []

  // 1. Flip the draft. Load-bearing — throw on failure.
  const { data: updated, error: draftErr } = await supabase
    .from('content_drafts')
    .update({
      posted_at: nowIso,
      posted_url: postedUrl,
      posted_external_id: externalId,
    })
    .eq('id', draftId)
    .is('posted_at', null) // idempotent
    .select(QUEUE_COLS)
    .maybeSingle()
  if (draftErr) throw draftErr

  // 2. Flip the idea status. Non-fatal; surface as warning.
  const { error: ideaErr } = await supabase
    .from('content_ideas')
    .update({ status: 'posted' })
    .eq('id', ideaId)
  if (ideaErr) {
    const msg = `Idea status didn't flip to 'posted' (${ideaErr.message}). Fix manually.`
    console.warn('markDraftPosted:', msg)
    warnings.push(msg)
  }

  // 3. Upsert content_posts so the analytics cron will refresh metrics.
  // YouTube path uses external_id as the unique key; non-YouTube posts
  // (Instagram, TikTok, etc.) don't have a reliable id-from-URL story
  // yet, so we skip the upsert and let Mason add them manually if he
  // wants analytics on those platforms.
  if (externalId && platform === 'youtube') {
    const { error: postErr } = await supabase.from('content_posts').upsert(
      {
        platform: 'youtube',
        external_id: externalId,
        link: postedUrl,
        posted_at: nowDate, // content_posts.posted_at is `date`, not timestamptz
      },
      { onConflict: 'platform,external_id', ignoreDuplicates: false }
    )
    if (postErr) {
      const msg = `Analytics tracking row not created (${postErr.message}). Mark posted will work but daily-analytics-pull won't refresh metrics on this video.`
      console.warn('markDraftPosted:', msg)
      warnings.push(msg)
    }
  } else if (platform !== 'youtube') {
    warnings.push(
      `${platform} analytics aren't auto-tracked. Add a content_posts row manually if you want metrics.`
    )
  }

  return { draft: updated, warnings }
}

// ── Posted-match prompts (auto-detect-posted-from-title-match) ────────

/**
 * Pending title-match candidates surfaced by the daily
 * propose_posted_matches() cron. Joined with the draft's idea (for the
 * title Mason wrote) and the candidate post (actual posted title + URL).
 * Sorted by similarity DESC so the strongest match is first.
 */
export async function fetchPendingPostedMatches({ limit = 20 } = {}) {
  const { data, error } = await supabase
    .from('posted_match_candidates')
    .select(
      `id, draft_id, post_id, similarity_score, proposed_at,
       draft:content_drafts!inner (
         id, idea_id, format, scheduled_platform, scheduled_at,
         idea:content_ideas!inner (id, title)
       ),
       post:content_posts!inner (
         id, external_id, platform, title, posted_at, link
       )`
    )
    .eq('status', 'pending')
    .order('similarity_score', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

/**
 * Mason confirmed: this posted video IS the draft. Reuses markDraftPosted
 * for the draft + idea + content_posts upsert, then flips the candidate
 * status to 'confirmed' so the cron won't re-surface it.
 */
export async function confirmPostedMatch({ matchId, draftId, ideaId, postedUrl, platform } = {}) {
  if (!matchId) throw new Error('matchId is required')
  const result = await markDraftPosted({ draftId, ideaId, postedUrl, platform })
  const { error } = await supabase
    .from('posted_match_candidates')
    .update({ status: 'confirmed', resolved_at: new Date().toISOString() })
    .eq('id', matchId)
  if (error) {
    console.warn('confirmPostedMatch: candidate status flip failed (non-fatal):', error)
  }
  return result
}

/**
 * Mason said "no, that's not it." Status flips to 'dismissed' so the
 * cron's `not exists` guard skips this pair on future runs.
 */
export async function dismissPostedMatch(matchId) {
  if (!matchId) throw new Error('matchId is required')
  const { error } = await supabase
    .from('posted_match_candidates')
    .update({ status: 'dismissed', resolved_at: new Date().toISOString() })
    .eq('id', matchId)
  if (error) throw error
}

/**
 * Mason's posted long-form videos eligible for atomization. Filtered to
 * platform='youtube' (the only platform with reliable transcript capture)
 * + (format='long-form' OR video_duration_seconds >= 240). The 240s
 * cutoff is intentional — YouTube Shorts cap at 180s but the API
 * sometimes reports 181-183s for a Short due to encoding padding.
 * Atomizing a Short returns shallow garbage derivatives. Step 12 audit
 * catch (originally 180s).
 */
export async function fetchPostedLongforms({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('content_posts')
    .select(
      'id, external_id, platform, title, posted_at, video_duration_seconds, format, transcript, views'
    )
    .eq('platform', 'youtube')
    .or('format.eq.long-form,video_duration_seconds.gte.240')
    .neq('format', 'shorts')
    .order('posted_at', { ascending: false, nullsFirst: false })
    .limit(limit)
  if (error) throw error
  return data || []
}
